<template>
  <!-- POC Cuidados Paliativos: local de cuidado do paciente. São as mesmas 3 etapas
       do quadro do Ambulatório (ambulatório, em casa, internação): mudar aqui move o
       card lá, e vice-versa. Dados da API (/api/palliative/patients/{id}/home-care). -->
  <section class="home-transfer">
    <h2 class="ht-title">
      Local de cuidado
    </h2>

    <p class="ht-status">
      <span
        class="ht-badge"
        :class="`is-${record.location}`"
      >{{ locationLabel }}</span>
      <span
        v-if="lastEvent"
        class="ht-muted"
      >
        {{ eventLabel(lastEvent.type) }}
        em {{ formatDateTime(lastEvent.at) }}{{ lastEvent.by ? ` por ${lastEvent.by}` : "" }}
      </span>
    </p>

    <!-- Em casa: mensagem enviada -->
    <div
      v-if="record.location === 'casa' && lastEvent && lastEvent.message"
      class="ht-sent"
    >
      <p class="ht-sent-title">
        Mensagem de boas-vindas
      </p>
      <p class="ht-sent-text">
        {{ lastEvent.message }}
      </p>
      <p class="ht-link-chip">
        {{ lastEvent.smsSent ? "Link do app Lila enviado por SMS" : "SMS não enviado" }}
      </p>
    </div>

    <p
      v-if="error"
      class="ht-error"
      role="alert"
    >
      {{ error }}
    </p>

    <lila-home-welcome-composer
      v-if="composing"
      :patient-name="patientName"
      @confirm="confirmHome"
      @cancel="composing = false"
    />

    <!-- As duas saídas possíveis a partir do local atual -->
    <div
      v-else
      class="ht-actions"
    >
      <lila-button
        v-if="record.location !== 'casa'"
        type="button"
        title="Enviar para casa"
        @custom-click-event="startComposing"
      />
      <lila-button
        v-if="record.location !== 'internacao'"
        type="button"
        title="Enviar para a internação"
        @custom-click-event="hospitalize"
      />
      <lila-button
        v-if="record.location !== 'ambulatorio'"
        type="button"
        :title="record.location === 'casa' ? 'Readmitir no ambulatório' : 'Voltar ao ambulatório'"
        @custom-click-event="readmit"
      />
    </div>
  </section>
</template>

<script>
import Button from "@components/shared/button/Button.vue";
import HomeWelcomeComposer from "@components/patients/HomeWelcomeComposer.vue";
import { eventBus } from "@/eventBus.js";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";
import {
  CARE_PLAN_UPDATED_EVENT,
  getHomeRecord,
  hospitalizePatient,
  readmitPatient,
  sendPatientHome
} from "@/services/palliative/carePlanApi";

const LOCATION_LABELS = {
  ambulatorio: "No ambulatório",
  casa: "Em casa",
  internacao: "Internado"
};

const EVENT_LABELS = {
  casa: "Enviado para casa",
  internacao: "Enviado para a internação",
  readmissao: "Voltou ao ambulatório"
};

export default {
  name: "PalliativeHomeTransfer",
  components: {
    "lila-button": Button,
    "lila-home-welcome-composer": HomeWelcomeComposer
  },
  // Mudança de local feita por outro profissional (ou no quadro do Ambulatório) atualiza esta tela.
  mixins: [pocLiveSync("load")],
  props: {
    patientId: {
      type: [String, Number],
      required: true
    },
    patientName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      record: { location: "ambulatorio", atHome: false, history: [] },
      composing: false,
      busy: false,
      error: ""
    };
  },
  computed: {
    locationLabel() {
      return LOCATION_LABELS[this.record.location] || LOCATION_LABELS.ambulatorio;
    },
    lastEvent() {
      const history = this.record.history || [];
      return history[history.length - 1] || null;
    }
  },
  watch: {
    patientId: {
      immediate: true,
      handler() {
        this.load();
      }
    }
  },
  methods: {
    async load() {
      const patientId = this.patientId;
      try {
        const record = await getHomeRecord(patientId);
        if (String(patientId) === String(this.patientId)) this.record = record;
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o local de cuidado.", error);
      }
    },
    startComposing() {
      this.composing = true;
    },
    async change(action) {
      if (this.busy) return false;
      this.busy = true;
      this.error = "";
      try {
        this.record = await action();
        eventBus.emit(CARE_PLAN_UPDATED_EVENT, { patientId: String(this.patientId) });
        return true;
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível mudar o local de cuidado.", error);
        this.error = error.message;
        // 409: o local já tinha mudado (por outro profissional, por exemplo).
        await this.load();
        return false;
      } finally {
        this.busy = false;
      }
    },
    async confirmHome(message) {
      if (await this.change(() => sendPatientHome(this.patientId, message))) this.composing = false;
    },
    readmit() {
      this.change(() => readmitPatient(this.patientId));
    },
    hospitalize() {
      this.change(() => hospitalizePatient(this.patientId));
    },
    eventLabel(type) {
      return EVENT_LABELS[type] || "";
    },
    formatDateTime(iso) {
      return this.$moment(iso).format("DD/MM/YYYY [às] HH:mm");
    }
  }
};
</script>

<style lang="scss" scoped>
.ht-error {
  margin: 0 0 12px;
  color: #db466f;
  font-size: 13px;
}

$primary: #4f3f82;
$primary-soft: #ece9f5;
$ink: #2b2b3a;
$muted: #6b6b7b;

.home-transfer {
  max-width: 560px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e4e2ea;

  :deep(.field) {
    margin-bottom: 0;
  }
}

.ht-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
  color: $primary;
}

.ht-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 16px;
}

.ht-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: currentColor;
  }

  &.is-casa {
    color: #1e6b3a;
    background-color: #e3f4e8;
  }

  &.is-ambulatorio {
    color: $primary;
    background-color: $primary-soft;
  }

  &.is-internacao {
    color: #9a5b00;
    background-color: #fff4e0;
  }
}

.ht-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ht-muted {
  font-size: 13px;
  color: $muted;
}

.ht-link-chip {
  display: inline-block;
  margin: 10px 0 0 20px;
  padding: 3px 10px;
  border: 1px dashed $primary;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: $primary;
}

.ht-sent {
  margin-bottom: 20px;
  padding: 14px 16px;
  border-left: 3px solid #1e6b3a;
  border-radius: 6px;
  background-color: #f3faf5;

  .ht-link-chip {
    margin-left: 0;
  }
}

.ht-sent-title {
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #1e6b3a;
}

.ht-sent-text {
  font-size: 14px;
  line-height: 20px;
  color: $ink;
  white-space: pre-line;
}
</style>
